import { useState } from 'react'
import { deleteMaterial, saveMaterial } from '../../services/products'
import type { Material, MaterialUnit, ProductCost } from '../../types'
import { amountInput, kurusToLiraInput, liraToKurus } from '../../utils/money'
import { UNIT_LABEL } from '../../utils/cost'
import { Select } from '../Select'
import { PlusIcon, TrashIcon, XIcon } from '../icons'
import { ConfirmDialog, type ConfirmSpec } from '../pos/ConfirmDialog'

interface Props {
  tenantId: string
  materials: Material[]
  costs: Map<string, ProductCost>
  onClose: () => void
}

interface Row {
  key: string
  id?: string
  name: string
  unit: MaterialUnit
  lira: string
}

const toRow = (m: Material): Row => ({
  key: m.id,
  id: m.id,
  name: m.name,
  unit: m.unit,
  lira: kurusToLiraInput(m.priceKurus),
})
const blank = (): Row => ({ key: crypto.randomUUID(), name: '', unit: 'kg', lira: '' })

/**
 * Hammaddeler (18 Eki 2026): maliyet hesabının ortak listesi, yalnız patron.
 * Fiyat burada değişince onu kullanan bütün ürünlerin maliyeti kendiliğinden
 * değişiyor; Kaydet yalnız değişen ve yeni satırları yazıyor.
 */
export function MaterialsDialog({ tenantId, materials, costs, onClose }: Props) {
  const [rows, setRows] = useState<Row[]>(() => (materials.length ? materials.map(toRow) : [blank()]))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ask, setAsk] = useState<ConfirmSpec | null>(null)

  const byId = new Map(materials.map((m) => [m.id, m]))
  /** Hammaddeyi reçetesinde kullanan ürün sayısı. */
  const usedBy = (id?: string) =>
    id ? [...costs.values()].filter((c) => c.mode === 'recipe' && c.recipe.some((l) => l.materialId === id)).length : 0

  const update = (key: string, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)))

  const remove = (r: Row) => {
    if (!r.id) {
      setRows((rs) => (rs.length > 1 ? rs.filter((x) => x.key !== r.key) : [blank()]))
      return
    }
    const n = usedBy(r.id)
    setAsk({
      title: `"${r.name}" silinsin mi?`,
      body: n
        ? `${n} ürünün maliyetinde kullanılıyor. Silinirse o ürünlerin maliyetine katılmaz.`
        : 'Hiçbir ürünün maliyetinde kullanılmıyor.',
      confirmLabel: 'Sil',
      danger: true,
      onConfirm: async () => {
        try {
          await deleteMaterial(tenantId, r.id!)
          setRows((rs) => (rs.length > 1 ? rs.filter((x) => x.key !== r.key) : [blank()]))
        } catch (err) {
          console.error('[maliyet] sil', err)
          setError('Silinemedi. İnternet bağlantınızı kontrol edin.')
        }
      },
    })
  }

  const save = async () => {
    // Tamamen boş yeni satırlar sayılmıyor.
    const filled = rows.filter((r) => r.id || r.name.trim() || r.lira.trim())
    for (const r of filled) {
      const price = liraToKurus(r.lira)
      if (!r.name.trim() || price === null || price < 0) {
        setError('Her satırda hammaddenin adını ve birim fiyatını yazın.')
        return
      }
    }
    const changed = filled.filter((r) => {
      const m = r.id ? byId.get(r.id) : undefined
      return !m || m.name !== r.name.trim() || m.unit !== r.unit || m.priceKurus !== liraToKurus(r.lira)
    })
    setBusy(true)
    setError(null)
    try {
      for (const r of changed) {
        await saveMaterial(tenantId, {
          id: r.id,
          name: r.name.trim(),
          unit: r.unit,
          priceKurus: liraToKurus(r.lira)!,
        })
      }
      onClose()
    } catch (err) {
      console.error('[maliyet] kaydet', err)
      setError('Kaydedilemedi. İnternet bağlantınızı kontrol edin.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="menu-modal" role="dialog" aria-modal="true" aria-label="Hammaddeler">
      <div className="menu-modal__card menu-modal__card--split mats">
        <header className="menu-modal__head">
          <h2>Hammaddeler</h2>
          <button type="button" className="menu-modal__x" onClick={onClose} aria-label="Kapat">
            <XIcon />
          </button>
        </header>

        <div className="mats__body">
          <p className="menu-modal__hint">
            Ürün maliyetinde kullandığınız hammaddeler ve birim fiyatları. Fiyatı burada
            değiştirdiğinizde onu kullanan bütün ürünlerin maliyeti kendiliğinden güncellenir.
          </p>

          <div className="mats__head" aria-hidden="true">
            <span>Hammadde</span>
            <span>Birim</span>
            <span>Birim fiyatı</span>
            <span />
          </div>
          <div className="mats__rows">
            {rows.map((r) => {
              const n = usedBy(r.id)
              return (
                <div className="mats__row" key={r.key}>
                  <div className="mats__name">
                    <input
                      value={r.name}
                      onChange={(e) => update(r.key, { name: e.target.value })}
                      placeholder="Örn. Kıyma"
                      aria-label="Hammadde adı"
                    />
                    {n > 0 && <small>{n} üründe kullanılıyor</small>}
                  </div>
                  <Select
                    value={r.unit}
                    onChange={(v) => update(r.key, { unit: v as MaterialUnit })}
                    aria-label="Birim"
                    options={[
                      { value: 'kg', label: 'kg' },
                      { value: 'adet', label: 'adet' },
                      { value: 'lt', label: 'lt' },
                    ]}
                  />
                  <div className="mats__price">
                    <input
                      value={r.lira}
                      onChange={(e) => update(r.key, { lira: amountInput(e.target.value) })}
                      inputMode="decimal"
                      placeholder="0"
                      aria-label={`1 ${UNIT_LABEL[r.unit]} fiyatı`}
                    />
                    <span>TL / {UNIT_LABEL[r.unit]}</span>
                  </div>
                  <button
                    type="button"
                    className="pcost__del"
                    onClick={() => remove(r)}
                    aria-label="Hammaddeyi sil"
                    title="Sil"
                  >
                    <TrashIcon />
                  </button>
                </div>
              )
            })}
          </div>

          <button
            type="button"
            className="btn btn--ghost btn--sm pcost__add"
            onClick={() => setRows((rs) => [...rs, blank()])}
          >
            <PlusIcon />
            Hammadde ekle
          </button>

          {error && <p className="menu-modal__error">{error}</p>}
        </div>

        <div className="menu-modal__buttons mats__buttons">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Vazgeç
          </button>
          <button type="button" className="btn btn--primary" onClick={() => void save()} disabled={busy}>
            {busy ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </div>
      </div>

      {ask && <ConfirmDialog {...ask} onClose={() => setAsk(null)} />}
    </div>
  )
}
